import { FailureKind } from '@sb/routine-runner/FailureKind';
import type { StepPlayArguments } from '@sb/routine-runner/Step/Step';
import Step from '@sb/routine-runner/Step/Step';
import { wait } from '@sb/utilities';

import { MoveVerticalLiftArguments } from './Arguments';
import { MoveVerticalLiftVariables } from './Variables';

export class MoveVerticalLiftStep extends Step<
  MoveVerticalLiftArguments,
  MoveVerticalLiftVariables
> {
  public static areSubstepsRequired = false;

  public static Arguments = MoveVerticalLiftArguments;

  public static Variables = MoveVerticalLiftVariables;

  public initializeVariableState(): void {
    this.variables = {};
  }

  // the play loop knows to clean up after `stop()` is called
  private stillGoing: boolean = false;

  // while we're paused, we don't want to send another gripper command
  private isPaused: boolean = false;

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    // Support either a preconstructed device command or an expression for targetHeight
    let { deviceCommand } = this.args as any;
    const targetHeightExpression = (this.args as any).targetHeight;

    const unitScaleToMeters = (this.args as any).unitScaleToMeters as
      | number
      | undefined;

    if (!deviceCommand && targetHeightExpression) {
      try {
        const evaluated = await this.routineFrame.evaluateExpression(
          targetHeightExpression,
          'Failed to evaluate vertical lift target height',
        );

        if (typeof evaluated !== 'number' || Number.isNaN(evaluated)) {
          return fail({
            failure: {
              kind: FailureKind.StepPlayFailure,
              stepKind: 'MoveVerticalLift',
            },
            failureReason:
              'Vertical lift target height expression did not produce a number',
          });
        }

        const heightMeters =
          unitScaleToMeters && unitScaleToMeters > 0
            ? evaluated * unitScaleToMeters
            : evaluated;

        deviceCommand = {
          kind: 'EwellixLiftTLTCommand',
          heightMeters,
          speedPercentage: 1,
        } as const;
      } catch (error) {
        return fail({
          failure: {
            kind: FailureKind.StepPlayFailure,
            stepKind: 'MoveVerticalLift',
          },
          failureReason: 'Failed to evaluate vertical lift target height',
          error,
        });
      }
    }

    this.stillGoing = true;

    // To start, this will basically just call `actuateGripper`.
    while (this.stillGoing) {
      // If `actuateDevice` gets interrupted by `pause()`'s `stopGripper`,
      // we want to loop until we've resumed, and we'll call `actuateDevice` again
      // when we resume.
      if (!this.isPaused) {
        try {
          await this.routineContext.actuateDevice(deviceCommand);
        } catch (error) {
          if (this.isPaused) {
            continue;
          }

          return fail({
            failure: {
              kind: FailureKind.StepPlayFailure,
              stepKind: 'MoveVerticalLift',
            },
            failureReason: 'Failed to move vertical lift',
            error,
          });
        }

        // If pause stopped it, we want to keep looping.
        // If pause didn't stop it, it either succeeded or `stop()` stopped it.
        // In either case, `play()` should now resolve.
        if (!this.isPaused) {
          break;
        }
      }

      await wait(100);
    }

    this.stillGoing = false;

    return undefined;
  }

  public async _pause(): Promise<void> {
    this.isPaused = true;
    await this.routineContext.stopGripper();
  }

  public _resume(): void {
    this.isPaused = false;
  }

  public async _stop(): Promise<void> {
    this.stillGoing = false;

    await this.routineContext.stopGripper();
  }

  public getSelfTimeout(): number {
    return 100_000;
  }
}
